import {
  APICallState,
  MFAActions,
  Selectors,
  openURL,
  useWidgetState,
  widgetDispatch,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {useDispatch, useSelector} from 'Provider';
import {useCallback, useEffect, useState} from 'react';
import {Platform} from 'react-native';

import {ProfileActions, isMFATokenValidSelector} from 'store/profile';
import {webViewQueryParam} from 'utils';
import {isWebViewSelector} from 'store/content';

export interface MFAStepUpHookType {
  onEdit: () => void;
  stopEditing: () => void;
  isEditing: boolean;
  mfaOptinStatus: boolean | undefined;
  isPanelUnlocked: boolean;
  isMFATokenValidationSuccess: boolean;
  triggerStepup: () => void;
  isMFAStepUpLoading: boolean;
}

export const useMFAStepUp = ({
  forwardUrl,
}: {
  forwardUrl: string;
}): MFAStepUpHookType => {
  const [isEditing, setIsEditing] = useState(false);
  const isWeb = Platform.OS === 'web';
  const isRaaSToken = useWidgetState(Selectors.isRaaSTokenSelector);
  const isWebView = useSelector(isWebViewSelector);
  const dispatch = useDispatch();
  const mfaOptinStatus = useWidgetState(Selectors.mfaOptinStatusSelector) as
    | boolean
    | undefined;
  const mfaOptinStatusState = useWidgetState(
    Selectors.mfaOptinStatusStateSelector
  ) as APICallState;
  const mfaTokenValidationStatus = useWidgetState(
    Selectors.mfaTokenValidationStateSelector
  ) as APICallState;
  const isPanelUnlocked = useSelector(isMFATokenValidSelector);
  const getIsLoadingState = () => {
    if (isWeb && !isRaaSToken && !isWebView) {
      return false;
    }

    return (
      [APICallState.LOADING].includes(mfaTokenValidationStatus) ||
      [APICallState.LOADING, APICallState.INITIAL].includes(mfaOptinStatusState)
    );
  };
  const isMFAStepUpLoading = getIsLoadingState();

  const isMFATokenValidationSuccess =
    mfaTokenValidationStatus === APICallState.SUCCESS;

  const triggerStepup = useCallback(() => {
    let stepUpUrl = `/a/verification/step-up/?forwardUrl=${forwardUrl}&amIAllowed=true`;
    if (isWebView) {
      stepUpUrl += '&' + webViewQueryParam;
    }

    // TODO: Remove amIAllowed=true from the URL when it is no longer necessary
    openURL(stepUpUrl);
  }, [forwardUrl]);

  const onEdit = useCallback(() => {
    setIsEditing(true);
    widgetDispatch(MFAActions.validateToken());
  }, []);

  const stopEditing = useCallback(() => {
    dispatch(ProfileActions.setIsMFATokenValid(false));
    setIsEditing(false);
  }, [dispatch]);

  useEffect(() => {
    if (mfaTokenValidationStatus === APICallState.SUCCESS && isEditing) {
      dispatch(ProfileActions.setIsMFATokenValid(true));
    } else if (mfaTokenValidationStatus === APICallState.ERROR && isEditing) {
      triggerStepup();
    }
  }, [dispatch, isEditing, mfaTokenValidationStatus, triggerStepup]);

  return {
    onEdit,
    stopEditing,
    isEditing,
    mfaOptinStatus,
    isPanelUnlocked,
    isMFATokenValidationSuccess,
    triggerStepup,
    isMFAStepUpLoading,
  };
};
